import { rgba } from 'polished';
import React from 'react';
import {Badge, OverlayTrigger, Tooltip} from 'react-bootstrap';
import styled from 'styled-components';
import { t } from 'ttag';
import useBasketContext from '../../../../../contexts/basket/useBasketContext';
import useOperatorContext from '../../../../../contexts/operator/useOperatorContext';
import useSiteContext from '../../../../../contexts/site/useSiteContext';
import { Item } from '../../../../../contexts/site/site-context';
import { money } from '../../../../../services/format';
import { isOfferOnMenu, isOfferOpenAt, offerBadge, offerNames, offerPriceOf, offersForItem, siteNow } from '../../../../../services/offers';
import {Preferences} from '../../MenuPage';
import {trackEvent} from "../../../../../services/api/operator";

type MenuItemProps = {
    item: Item;
    showItemModal: (item: Item, edit: boolean) => void;
    preferences: Preferences;
    variant?: string;
    fromUpsell?: boolean;
};

const MenuItem = ({ item, showItemModal, preferences, variant, fromUpsell }: MenuItemProps) => {
    const basketContext = useBasketContext();
    const operatorContext = useOperatorContext();
    const siteContext = useSiteContext();

    const basketItemCount = () => {
        let count = 0;
        basketContext.basketItems
            .filter((i) => item.uid === i.uid)
            .forEach((i) => count += (i.quantity ?? 0));
        return count;
    };

    const format = (pence: number) => money(pence, operatorContext.operator?.ccy, operatorContext.operator?.locale);

    // The offers that are on this item now: its price may be reduced, or it may be free with something.
    const now = siteNow(siteContext.site?.timezone);
    const itemOffers = offersForItem(siteContext.offers, item.uid, siteContext.menu).filter((offer) => isOfferOnMenu(offer, siteContext.menu) && isOfferOpenAt(offer, now));
    const offerPrice = itemOffers.length ? offerPriceOf(itemOffers, item) : null;

    const renderPrice = () => offerPrice !== null ? (
        <>
            <span className="menu-item__price menu-item__price--offer">{format(offerPrice)}</span>
            <WasPrice className="menu-item__price menu-item__price--before">{format(item.price)}</WasPrice>
        </>
    ) : (
        <span className="menu-item__price">{format(item.price)}</span>
    );

    const renderTitleBadges = () => {
        let badges = [];
        itemOffers.forEach((offer) => {
            badges.push(
                <Badge key={`tfbg-${item.uid}-${offer.uid}`} className="badge--title badge--title--offer">
                    <div className="badge--title__icon material-symbols-outlined material-symbols-outlined--filled">
                        kid_star
                    </div>
                    <span className={'badge--title__text'}>
                        {offerBadge(offer, offerNames(offer, siteContext.menu), format)}
                    </span>
                </Badge>,
            );
        });
        if (item.is_new) {
            badges.push(
                <Badge key={`tfbg-${item.uid}-new`} className="badge--title badge--title--new">
                    <div className="badge--title__icon material-symbols-outlined material-symbols-outlined--filled">
                        kid_star
                    </div>
                    <span className={'badge--title__text'}>
                        {t`New`}
                    </span>
                </Badge>,
            );
        }
        if (item.is_popular) {
            badges.push(
                <Badge key={`tfbg-${item.uid}-popular`} className="badge--title badge--title--popular">
                    <div className="badge--title__icon material-symbols-outlined material-symbols-outlined--filled">
                        kid_star
                    </div>
                    <span className={'badge--title__text'}>
                        {t`Popular`}
                    </span>
                </Badge>,
            );
        }
        if (item.is_offer) {
            badges.push(
                <Badge key={`tfbg-${item.uid}-offer`} className="badge--title badge--title--offer">
                    <div className="badge--title__icon material-symbols-outlined material-symbols-outlined--filled">
                        kid_star
                    </div>
                    <span className={'badge--title__text'}>
                        {t`Meal deal`}
                    </span>
                </Badge>,
            );
        }
        return badges.map((badge) => badge);
    };

    const renderDietBadges = () => {
        let badges = [];
        if (item.is_vegan) {
            badges.push(
                <OverlayTrigger
                    key={'veganPlacement'}
                    placement={'top'}
                    overlay={
                        <Tooltip id={`tooltip-veganPlacement`}>
                            Suitable for Vegans
                        </Tooltip>
                    }
                >
                    <Badge className="badge--menu-item badge--menu-item--circular" key={`tfbg-${item.uid}-vegan`} title="Vegan">
                        Vg
                    </Badge>
                </OverlayTrigger>
            );
        }

        if (item.is_vegetarian) {
            badges.push(
                <OverlayTrigger
                    key={'vegetarianPlacement'}
                    placement={'top'}
                    overlay={
                        <Tooltip id={`tooltip-vegetarianPlacement`}>
                            Suitable for Vegetarians
                        </Tooltip>
                    }
                >
                    <Badge className="badge--menu-item badge--menu-item--circular" key={`tfbg-${item.uid}-vegetarian`} title="Vegetarian">
                        V
                    </Badge>
                </OverlayTrigger>
            );
        }

        if (item.is_gluten_free) {
            badges.push(
                <OverlayTrigger
                    key={'gfPlacement'}
                    placement={'top'}
                    overlay={
                        <Tooltip id={`tooltip-gfPlacement`}>
                            Gluten-Free
                        </Tooltip>
                    }
                >
                    <Badge className="badge--menu-item badge--menu-item--circular" key={`tfbg-${item.uid}-gf`} title="Gluten-Free">
                        Gf
                    </Badge>
                </OverlayTrigger>
            );
        }

        if (item.is_halal) {
            badges.push(
                <OverlayTrigger
                    key={'halalPlacement'}
                    placement={'top'}
                    overlay={
                        <Tooltip id={`tooltip-halalPlacement`}>
                            Halal
                        </Tooltip>
                    }
                >
                    <Badge className="badge--menu-item badge--menu-item--circular" key={`tfbg-${item.uid}-halal`} title="Halal">
                        H
                    </Badge>
                </OverlayTrigger>
            );
        }

        if (item.is_kosher) {
            badges.push(
                <OverlayTrigger
                    key={'kosherPlacement'}
                    placement={'top'}
                    overlay={
                        <Tooltip id={`tooltip-kosherPlacement`}>
                            Kosher
                        </Tooltip>
                    }
                >
                    <Badge className="badge--menu-item badge--menu-item--circular" key={`tfbg-${item.uid}-kosher`} title="Kosher">
                        K
                    </Badge>
                </OverlayTrigger>
            );
        }

        // if (item.allergens) {
        //     badges.push(<Badge key={`tfbg-${item.uid}-allergens`} variant="secondary">Allergens</Badge>)
        // }

        if (badges.length > 0) {
            return <>{badges.map((badge) => badge)}</>;
        }
        return null;
    };

    if ( item.is_vegan && !preferences.vegan ) { return null; }
    if ( item.is_vegetarian && !preferences.vegetarian ) { return null; }
    if ( item.is_gluten_free && !preferences.glutenFree ) { return null; }
    if ( item.is_halal && !preferences.halal ) { return null; }
    if ( item.is_kosher && !preferences.kosher ) { return null; }

    if ( item.allergens_list ) {
        let overlap = item.allergens_list.filter((allergen: string) => {
            return preferences[allergen as keyof Preferences]
        });
        if ( overlap.length > 0 ) { return null; }
    }

    const styleVariant = variant ?? operatorContext.operator?.theme?.v4.card_style ?? 'landscape';

    return styleVariant === 'portrait' ? (
        <StyledMenuItemPortraitWrapper>
            <StyledMenuItemPortrait
                className={`menu-item__wrapper ${item.image ? '' : 'no-image'} ${item.is_hidden ? 'unavailable-item' : ''}`}
                inBasket={basketItemCount() > 0}
                onClick={() => {
                	trackEvent('open-item', siteContext.site?.uid ?? '')
                	showItemModal(item, false)
            	}}
                variant={styleVariant}
                hasImage={!!item.image}
                fromUpsell={fromUpsell}
            >
                    {item.is_hidden ? ( <div className="unavailable-item-overlay" /> ) : null}
                    {item.image ? <CardImage image={item.image} className="menu-item__image" /> : null}

                    <div className="menu-item__content--portrait">
                        <div className="menu-item__top">
                            <div className="menu-item__header">
                                {basketItemCount() > 0 && basketItemCount() ? (
                                    <span className={'menu-item__basket-amount' + (item.image ? ' menu-item__basket-amount--overlay' : '')}>
                                        {basketItemCount() > 0 && basketItemCount()}
                                    </span>
                                ) : null }
                                <h3 className="menu-item__title">
                                    {item.name}
                                </h3>
                            </div>
                            <div className="menu-item__metadata">
                                {renderPrice()}
                                { (item.calories !== '' && item.calories && preferences.calories) && (
                                    <span className="menu-item__calories-wrapper">
                                        &nbsp;&middot;&nbsp;
                                        <span className="menu-item__calories">
                                            {item.calories} kcal
                                        </span>
                                    </span>
                                ) }
                                { renderDietBadges() && (
                                    <>
                                        &nbsp;&middot;&nbsp;
                                        <span className="menu-item__diet-badges">
                                            { renderDietBadges() }
                                        </span>
                                    </>
                                ) }
                            </div>
                            <div className="menu-item__details">
                                {item.description !== '' ? (
                                    <span className="menu-item__details__description">{item.description}</span>
                                ) : null}
                            </div>
                        </div>
                        <div className="menu-item__footer">
                            { item.is_hidden && (
                                <span className="unavailable-item-text">
                                    {t`Unavailable`}
                                </span>
                            )}
                            { !item.is_hidden && renderTitleBadges() && (
                                <span className="menu-item__title-badges">
                                    { renderTitleBadges() }
                                </span>
                            ) }
                        </div>
                    </div>
            </StyledMenuItemPortrait>
        </StyledMenuItemPortraitWrapper>
        ) : (

        /**
         * inBasket prop if item is in basket
         */

        <StyledMenuItem
            className={`menu-item__wrapper ${item.image ? '' : 'no-image'} ${item.is_hidden ? 'unavailable-item' : ''}`}
            inBasket={basketItemCount() > 0}
            onClick={() => {
                trackEvent('open-item', siteContext.site?.uid ?? '')
                showItemModal(item, false)
            }}
            variant={styleVariant}
        >
            {item.is_hidden ? ( <div className="unavailable-item-overlay" /> ) : null}
            <MenuItemContent className="menu-item" variant={styleVariant}>
                <div className="menu-item__header">
                    {/**
                     * Change item count in basket
                     */}
                    {basketItemCount() > 0 && basketItemCount() ? (
                         <span className={'menu-item__basket-amount' + (item.image ? ' menu-item__basket-amount--overlay' : '')}>
                             {basketItemCount() > 0 && basketItemCount()}
                         </span>
                     ) : null }
                    <h3 className="menu-item__title">
                        {item.name}
                    </h3>
                </div>
                <div className="menu-item__metadata">
                    {renderPrice()}
                    { (item.calories !== '' && item.calories && preferences.calories) && (
                        <span className="menu-item__calories-wrapper">
                            &nbsp;&middot;&nbsp;
                            <span className="menu-item__calories">
                                {item.calories} kcal
                            </span>
                        </span>
                    ) }
                    { renderDietBadges() && (
                        <>
                            &nbsp;&middot;&nbsp;
                            <span className="menu-item__diet-badges">
                                { renderDietBadges() }
                            </span>
                        </>
                    ) }
                </div>
                <div className="menu-item__details">
                    {item.description !== '' ? (
                        <span className="menu-item__details__description">{item.description}</span>
                    ) : null}
                </div>
                <div className="menu-item__footer">
                    { item.is_hidden && (
                        <span className="unavailable-item-text">
                            {t`Unavailable`}
                        </span>
                    )}
                    { !item.is_hidden && renderTitleBadges() && (
                        <span className="menu-item__title-badges">
                            { renderTitleBadges() }
                        </span>
                    ) }
                </div>
            </MenuItemContent>
            {item.image ? <CardImage image={item.image} className="menu-item__image" /> : null}
        </StyledMenuItem>
    );
};

interface ImageProps {
    image: string | null;
}

// the price before an offer, struck through (in both layouts of the card, which style their own prices)
const WasPrice = styled.span`
    && {
        text-decoration: line-through;
        color: var(--text-grey);
        font-weight: 400;
    }
`;

const MenuItemContent = styled.div<{ variant?: string }>`
    ${(props) => props.variant === 'landscape' && `
        width: calc(100% - 114px - 16px);
    `}

    display: flex;
    flex-direction: column;
    gap: 4px;

    .menu-item {
        &__header {
            display: flex;
            flex-direction: column;
        }

        &__title {
            margin-bottom: 0;
            vertical-align: middle;
            overflow: hidden;
            text-overflow: ellipsis;
            font-family: var(--text_body_font);
            letter-spacing: var(--text_body_font_spacing);
            text-transform: var(--text_body_font_case);
            color: var(--text_subsubheading_color);

            @supports (-webkit-line-clamp: 1) {
                white-space: initial;
                display: -webkit-inline-box;
                -webkit-line-clamp: 1;
                -webkit-box-orient: vertical;
            }
        }

        &__price {
            font-size: 14px;
            line-height: 20px;
            color: var(--text-black);

            &--before {
                text-decoration: line-through;
                margin-left: 8px;
                color: var(--text-grey);
                font-weight: 400;
            }
        }

        .unavailable-item-text {
            color: var(--text-black);
            padding: 0;
            margin-left: 0;
            font-weight: 500;
            font-size: 14px;
            line-height: 20px;
            text-overflow: ellipsis;
            white-space: nowrap;
            overflow: hidden;
            display: inline-block;
            @media screen and (min-width: 768px) {
                font-size: 16px;
                line-height: 24px;
            }
        }

        &__metadata {
            display: flex;
            align-items: center;
            gap: 4px;
            font-size: 14px;
            line-height: 20px;
            color: var(--text-black);
        }

        &__calories-wrapper {
            @media screen and (max-width: 767px) {
                display: none;
            }
        }

        @media screen and (max-width: 1140px) {
            display: -webkit-box;
            -webkit-line-clamp: 1;
            -webkit-box-orient: vertical;
            overflow: hidden;
        }

        &__details {
            white-space: nowrap;
            text-overflow: ellipsis;
            font-size: 14px;
            margin: 0;
            flex-grow: 1;

            &__description {
                font-size: 14px;
                line-height: 20px;
                color: var(--text-grey);
                overflow: hidden;
                @supports (-webkit-line-clamp: 2) {
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: initial;
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                }
            }
        }

        &__footer {
            display: flex;
            gap: 8px;
            line-height: 1;
            align-items: center;
        }
    }
`;

const StyledMenuItem = styled.div<{ inBasket?: boolean, variant?: string }>`
    position: relative;
    height: 148px;
    background: var(--color_ui_7);
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    gap: 16px;
    cursor: pointer;
    padding: 16px 16px 14px 16px;
    border: var(--ui_card_border);
    transition: box-shadow 0.2s ease-in-out;

    @media screen and (max-width: 767px) {
        &:not(:first-child) {
            border-top-width: 0;
        }
    }

    @media screen and (min-width: 768px) {
        border-radius: var(--radius_xl);
    }

    .menu-item__basket-amount {
        width: 20px;
        height: 20px;
        display: ${(props) => (!props.inBasket ? 'none' : 'inline-flex !important')};
        justify-content: center;
        align-items: center;
        color: #FFFFFF;
        background: var(--color_ui_5);
        font-size: 12px;
        line-height: 16px;
        padding: 2px;
        border-radius: 8px;
        margin-right: 8px;
        margin-bottom: 8px;

        &--overlay {
            position: absolute;
            top: 35px;
            right: 98px;
            margin: 0;
        }
    }

    ${(props) => props.variant === 'landscape' && `
        @media screen and (min-width: 768px) {
            box-shadow: 0 -1px 0 var(--border-grey), 0 1px 0 var(--border-grey);
        }

        &.no-image .menu-item {
            max-width: 100% !important;
            width: auto !important;
        }
    `}

    ${(props) => props.theme.v4.card_shadow === 'shadow' && `
        @media screen and (min-width: 768px) {
            &:not(.unavailable-item) {
                box-shadow: 0px 4px 10px -4px rgb(0 0 0 / 10%), 0 2px 4px -2px rgba(0 0 0 / 5%), 0 0px 2px rgba(0 0 0 / 5%);
            }
            &:hover { box-shadow: 0 22px 24px 0 rgb(0 0 0 / 8%); }
        }
    `}

    ${(props) => props.theme.v4.card_shadow === 'flat' && `
        @media screen and (min-width: 768px) {
            box-shadow: none;
            &:hover { box-shadow: 0 22px 24px 0 rgb(0 0 0 / 8%); }
        }
    `}

    .unavailable-item-overlay {
        position: absolute;
        inset: 0 0 1px 0;
        background-color: rgba(255, 255, 255, 0.5);
        z-index: 9;
        border-radius: var(--radius_xl);
    }

    &.unavailable-item:hover {
        box-shadow: none;
        cursor: default;
    }
`;

const CardImage = styled.div<ImageProps>`
    background: center center no-repeat;
    background-size: cover;
    width: 114px;
    height: 114px;
    margin: 0;
    ${(props) =>
        props.image
            ? `background-image: url('${props.image}'); background-color: transparent;`
            : null};
    flex-shrink: 0;
    border-radius: var(--radius_xl);
`;


const StyledMenuItemPortrait = styled.div<{ inBasket?: boolean, variant?: string, hasImage: boolean | '', fromUpsell?: boolean }> `
    position: relative;
    gap: 0;
    height: 290px;
    cursor: pointer;
    border-radius: var(--radius_xl);
    border: var(--ui_card_border);
    overflow: hidden;
    background: var(--color_ui_7);
    transition: box-shadow 0.2s ease-in-out;

    ${(props) => !props.fromUpsell && `
        @media screen and (min-width: 768px) {
        	height: 440px;
    	}
    `}

    .menu-item__basket-amount {
        width: 20px;
        height: 20px;
        display: ${(props) => (!props.inBasket ? 'none' : 'inline-flex !important')};
        justify-content: center;
        align-items: center;
        color: #FFFFFF;
        background: var(--color_ui_5);
        font-size: 12px;
        line-height: 16px;
        padding: 2px;
        border-radius: var(--radius_s);
        margin-right: 8px;
        margin-bottom: 8px;

        &--overlay {
            position: absolute;
            top: 24px;
            left: 24px;
            margin: 0;

			${(props) => !props.fromUpsell && `
            	@media screen and (min-width: 768px) {
                	top: 28px;
                	left: 28px;
            	}
            `}
        }
    }

    ${(props) => props.theme.v4.card_shadow === 'shadow' && `
        &:not(.unavailable-item) {
            box-shadow: 0px 4px 10px -4px rgb(0 0 0 / 10%), 0 2px 4px -2px rgba(0 0 0 / 6%), 0 0px 2px rgba(0 0 0 / 6%);
            &:hover {
                box-shadow: 0 12px 20px 0 rgb(0 0 0 / 10%) !important;
            }
        }
    `}


    ${(props) => props.theme.v4.card_shadow === 'flat' && `
        box-shadow: none;
        &:not(.unavailable-item) {
            &:hover {
                box-shadow: 0 12px 20px 0 rgb(0 0 0 / 10%) !important;
            }
        }

    `}

    .menu-item__image {
        width: calc(100% - 24px);
        height: 147px;
        margin: 12px 12px 0 12px;
        flex-grow: 1;
        flex-shrink: 1;

        ${(props) => !props.fromUpsell && `
        	@media screen and (min-width: 768px) {
            	width: calc(100% - 32px);
            	height: 248px;
            	margin: 16px 16px 0 16px;
        	}
        `}
    }

    &.no-image {
        justify-content: flex-start;
        .menu-item__content--portrait {
            flex-grow: 1;
            height: 286px;

            ${(props) => !props.fromUpsell && `
            	@media screen and (min-width: 768px) {
                	height: 440px;
            	}
            `}
        }
        .menu-item__top {
            flex-grow: 0;
        }
    }

    .menu-item__content--portrait {
        display: flex;
        flex-direction: column;
        padding: 12px;
        flex-basis: 101px;
        height: 128px;

        ${(props) => !props.fromUpsell && `
        	@media screen and (min-width: 768px) {
            	flex-basis: 104px;
            	padding: 16px;
            	flex-grow: 0;
            	height: 175px;
        	}
        `}

        order: 2;
        .menu-item {
            &__header {
                display: flex;
                flex-direction: column;
                @media screen and (max-width: 479px) {
                    margin-bottom: 0;
                }
            }
            &__top {
                flex-grow: 1;
                display: flex;
                flex-direction: column;
                flex-basis: 67px;
                gap: 4px;

                ${(props) => !props.fromUpsell && `
                	@media screen and (min-width: 768px) {
                    	height: 112px;
                	}
                `}
            }
            &__title {
                margin: 0;
                overflow: hidden;
                vertical-align: middle;
                font-family: var(--text_body_font);
                letter-spacing: var(--text_body_font_spacing);
                text-transform: var(--text_body_font_case);
                color: var(--text_subsubheading_color);
                display: -webkit-box;
                -webkit-line-clamp: 2;
                -webkit-box-orient: vertical;
            }
            &__metadata {
                display: flex;
                gap: 4px;
                font-size: 14px;
                line-height: 20px;
                color: var(--text-black);
            }
            &__calories-wrapper {
            	${(props) => props.fromUpsell && 'display: none;'}

                @media screen and (max-width: 767px) {
                    display: none;
                }
            }
            &__details {
                &__description {
                    display: none;

                    ${(props) => !props.fromUpsell && `
                    	@media screen and (min-width: 768px) {
                        	display: block;
                        	font-size: 14px;
                        	line-height: 20px;
                        	color: var(--text-grey);
                        	overflow: hidden;

                        	@supports (-webkit-line-clamp: 2) {
                            	overflow: hidden;
                            	text-overflow: ellipsis;
                            	white-space: initial;
                            	display: -webkit-box;
                            	-webkit-line-clamp: 2;
                            	-webkit-box-orient: vertical;
                        	}
                    	}
                    `}
                }
            }
            &__footer {
                display: flex;
                gap: 5%;
                align-items: center;
                line-height: 0;

                ${(props) => !props.fromUpsell && `
                	@media screen and (min-width: 768px) {
                    	gap: 8px;
                	}
                `}

                .unavailable-item-text {
                    font-size: 14px;
                    line-height: 20px;
                }
            }
        }
    }
    .unavailable-item-overlay {
        position: absolute;
        top: -1px;
        bottom: -1px;
        left: -1px;
        right: -1px;
        width: calc(100% + 2px);
        height: calc(100% + 2px);
        background-color: rgba(255,255,255,0.5);
        z-index: 9;
        border-radius: var(--radius_xl);
    }

    &.unavailable-item:hover {
        box-shadow: none;
        cursor: default;
    }
`;

const StyledMenuItemPortraitWrapper = styled.div`
    margin: 0;
`;

export default MenuItem;
